import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { IMAGES } from "@/lib/constants";

const VALUES = [
  { title: "Craft over volume", body: "Ten considered profiles beat a hundred hopeful ones. Every shortlist is small on purpose." },
  { title: "Radical honesty", body: "If your spec is wrong, we say so. If a candidate isn't right, we say that too — to both sides." },
  { title: "Speed as respect", body: "Candidates get answers in days, clients get shortlists in 48 hours. Nobody waits in the dark." },
  { title: "After the offer", body: "Onboarding check-ins at 30, 60, 90 days. A placement isn't done when the letter is signed." },
];

const TIMELINE = [
  { year: "2024", event: "Founded in Hyderabad by recruiters who wanted to fix agency hiring." },
  { year: "2025", event: "Crossed 150 placements; launched contract staffing and leadership search desks." },
  { year: "2026", event: "500+ placements across 8 industries, with clients in 6 cities and counting." },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-40">
      <Seo
        title="About Samanvay HR — A Recruitment Studio, Not a Resume Factory"
        description="Samanvay HR runs fewer searches, deeper. Learn how our senior recruiters deliver considered shortlists, honest counsel and hires that stay."
      />
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <SectionTag>About Samanvay HR</SectionTag>
        <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-slate-900 max-w-4xl">
          <MaskLine delay={0.15}>A recruitment studio,</MaskLine>
          <MaskLine delay={0.3}>
            <span>not a <span className="font-editorial italic font-medium text-brand">resume factory.</span></span>
          </MaskLine>
        </h1>
        <FadeUp delay={0.55} className="mt-10 max-w-2xl">
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            We started Samanvay HR because hiring had become a numbers game — agencies optimizing for
            volume, companies drowning in mismatched profiles, candidates treated like inventory.
            We run fewer searches, deeper. It works.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <FadeUp className="lg:col-span-7">
            <div className="relative h-full min-h-[420px] overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
              <img src={IMAGES.team} alt="Samanvay HR team at work" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-editorial italic text-2xl text-white/90">&ldquo;Hiring is the highest-leverage decision a company makes. We treat it that way.&rdquo;</p>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="h-full rounded-3xl border border-slate-200 bg-white p-10 flex flex-col justify-between shadow-sm">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-brand">By the numbers</p>
                <div className="mt-8 space-y-8">
                  {[
                    ["500+", "placements since 2024"],
                    ["40+", "retained clients"],
                    ["96%", "offer acceptance"],
                    ["92%", "12-month retention"],
                  ].map(([v, l]) => (
                    <div key={l} className="flex items-baseline justify-between border-b border-slate-100 pb-6">
                      <span className="font-display text-4xl font-semibold text-slate-900">{v}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-400 text-right">{l}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Link to="/contact" data-testid="about-talk-btn" className="group mt-10 inline-flex items-center gap-2 text-sm text-brand">
                Talk to us <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <SectionTag>How We Work</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">Values with teeth.</h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-10 transition-shadow duration-300 hover:shadow-glow-sm" data-testid={`value-${i}`}>
                  <p className="font-editorial italic text-4xl text-brand/80">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight text-slate-900">{v.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-32" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <SectionTag>The Story So Far</SectionTag>
          </FadeUp>
          <div className="mt-10 space-y-0">
            {TIMELINE.map((t, i) => (
              <FadeUp key={t.year} delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-6 border-t border-slate-200 py-12 items-baseline" data-testid={`timeline-${t.year}`}>
                  <p className="col-span-12 sm:col-span-3 font-display text-5xl font-semibold text-slate-900/90">{t.year}</p>
                  <p className="col-span-12 sm:col-span-8 sm:col-start-5 text-slate-600 leading-relaxed text-lg">{t.event}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
            <img src={IMAGES.office} alt="Our workspace" className="h-[380px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/50 to-transparent flex items-center">
              <div className="px-10 sm:px-16 max-w-xl">
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
                  Small team. Senior recruiters. Zero handoffs.
                </h2>
                <p className="mt-4 text-slate-300 text-sm leading-relaxed">
                  The person who scopes your role is the person who fills it. That&rsquo;s the whole trick.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
